import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ScreenLock from './components/ScreenLock/ScreenLock';
import { ConfigProvider } from 'antd';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import EmployeeManagement from './pages/EmployeeManagement/EmployeeManagement';
import RecruitmentHiring from './pages/RecruitmentHiring/RecruitmentHiring';
import EmployeeOnboarding from './pages/EmployeeOnboarding/EmployeeOnboarding';
import PerformanceManagement from './pages/PerformanceManagement/PerformanceManagement';
import TrainingDevelopment from './pages/TrainingDevelopment';
import LeaveManagement from './pages/LeaveManagement';
import SettingsAdministration from './pages/Settings/SettingsAdministration';
import DocumentManagement from './pages/DocumentManagement';
import PayrollDashboard from './pages/Payroll/PayrollDashboard';
import BenefitsManagement from './pages/Benefits/BenefitsManagement';
import AnalyticsDashboard from './pages/Analytics/AnalyticsDashboard';
import ReportsDashboard from './pages/Reports/ReportsDashboard';
import DisciplinaryManagement from './pages/Disciplinary/DisciplinaryManagement';
import ExitManagement from './pages/ExitManagement/ExitManagement';
import AttendanceManagement from './pages/Attendance/AttendanceManagement';
import OrgChartPage from './pages/OrgChart/OrgChartPage';
import Layout from './components/Layout/Layout';
import Register from './pages/Register/Register';
import NexusLink from './pages/Link/NexusLink';
import ClientManagement from './pages/ClientManagement/ClientManagement';
import EngagementDashboard from './pages/Engagement/EngagementDashboard';
import CompensationManagement from './pages/Compensation/CompensationManagement';
import SuccessionPlanning from './pages/Succession/SuccessionPlanning';
import HelpDeskDashboard from './pages/Helpdesk/HelpDeskDashboard';
import StatutoryRemittance from './pages/Statutory/StatutoryRemittance';
import IntegrationsHub from './pages/Integrations/IntegrationsHub';
import EmployeeDashboard from './pages/EmployeeDashboard/EmployeeDashboard';
import LandingPage from './pages/Landing/LandingPage';
import './App.css';

// Theme configuration for professional look
const theme = {
  token: {
    colorPrimary: '#1890ff',
    colorSuccess: '#52c41a',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
    fontSize: 14,
    borderRadius: 8,
  },
};

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '16px'
      }}>
        Loading Nexus HR System...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check role-based access
  if (requiredRole && user && !requiredRole.includes(user.role)) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <h2>Access Denied</h2>
        <p>You don't have permission to access this resource.</p>
      </div>
    );
  }

  return <>{children}</>;
};

// Public Route Component (redirects to dashboard if already logged in)
interface PublicRouteProps {
  children: React.ReactNode;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '16px'
      }}>
        Loading...
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Main App Routes
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      
      <Route 
        path="/register" 
        element={<Register />} 
      />

      {/* HR Consultant — Client Management */}
      <Route
        path="/clients"
        element={
          <ProtectedRoute requiredRole={['hr_consultant','hr_consultant_admin','superadmin']}>
            <Layout>
              <ClientManagement />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Nexus BMP Handoff Route (Option C - Path A) */}
      <Route path="/link" element={<NexusLink />} />

      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/employees" 
        element={
          <ProtectedRoute requiredRole={['hr_manager', 'admin', 'hr_consultant', 'hr_consultant_admin', 'superadmin']}>
            <Layout>
              <EmployeeManagement />
            </Layout>
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/recruitment" 
        element={
          <ProtectedRoute requiredRole={['hr_manager', 'admin', 'superadmin']}>
            <Layout>
              <RecruitmentHiring />
            </Layout>
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/onboarding" 
        element={
          <ProtectedRoute requiredRole={['hr_manager', 'admin', 'superadmin']}>
            <Layout>
              <EmployeeOnboarding />
            </Layout>
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/performance" 
        element={
          <ProtectedRoute>
            <Layout>
              <PerformanceManagement />
            </Layout>
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/training" 
        element={
          <ProtectedRoute>
            <Layout>
              <TrainingDevelopment />
            </Layout>
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/leave" 
        element={
          <ProtectedRoute>
            <Layout>
              <LeaveManagement />
            </Layout>
          </ProtectedRoute>
        } 
      />

      {/* NEW: Payroll Route */}
      <Route 
        path="/payroll" 
        element={
          <ProtectedRoute requiredRole={['hr_manager', 'admin', 'superadmin']}>
            <Layout>
              <PayrollDashboard />
            </Layout>
          </ProtectedRoute>
        } 
      />

      {/* NEW: Benefits Route */}
      <Route 
        path="/benefits" 
        element={
          <ProtectedRoute requiredRole={['hr_manager', 'admin', 'superadmin']}>
            <Layout>
              <BenefitsManagement />
            </Layout>
          </ProtectedRoute>
        } 
      />

      {/* NEW: Analytics Route */}
      <Route 
        path="/analytics" 
        element={
          <ProtectedRoute requiredRole={['admin', 'superadmin']}>
            <Layout>
              <AnalyticsDashboard />
            </Layout>
          </ProtectedRoute>
        } 
      />

      {/* NEW: Reports Route */}
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute requiredRole={['admin', 'superadmin']}>
            <Layout>
              <ReportsDashboard />
            </Layout>
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/settings" 
        element={
          <ProtectedRoute requiredRole={['admin', 'superadmin']}>
            <Layout>
              <SettingsAdministration />
            </Layout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/documents" 
        element={
          <ProtectedRoute requiredRole={['staff', 'hr_manager', 'admin', 'superadmin']}>
            <Layout>
              <DocumentManagement />
            </Layout>
          </ProtectedRoute>
        } 
      />

      {/* Employee Self-Service Dashboard */}
      <Route
        path="/my-dashboard"
        element={
          <ProtectedRoute>
            <EmployeeDashboard />
          </ProtectedRoute>
        }
      />

      {/* Engagement */}
      <Route path="/engagement" element={<ProtectedRoute requiredRole={['hr_manager','admin','superadmin','hr_consultant','hr_consultant_admin']}><Layout><EngagementDashboard /></Layout></ProtectedRoute>} />

      {/* Compensation */}
      <Route path="/compensation" element={<ProtectedRoute requiredRole={['hr_manager','admin','superadmin']}><Layout><CompensationManagement /></Layout></ProtectedRoute>} />

      {/* Succession Planning */}
      <Route path="/succession" element={<ProtectedRoute requiredRole={['hr_manager','admin','superadmin']}><Layout><SuccessionPlanning /></Layout></ProtectedRoute>} />

      {/* HR Helpdesk */}
      <Route path="/helpdesk" element={<ProtectedRoute><Layout><HelpDeskDashboard /></Layout></ProtectedRoute>} />

      {/* Statutory Remittances */}
      <Route path="/statutory" element={<ProtectedRoute requiredRole={['admin','superadmin']}><Layout><StatutoryRemittance /></Layout></ProtectedRoute>} />

      {/* Integrations */}
      <Route path="/integrations" element={<ProtectedRoute requiredRole={['admin','superadmin']}><Layout><IntegrationsHub /></Layout></ProtectedRoute>} />

      {/* Default redirects */}
      {/* Disciplinary & Grievance */}
      <Route path="/disciplinary" element={<ProtectedRoute requiredRole={['hr_manager','admin','superadmin','hr_consultant_admin']}><Layout><DisciplinaryManagement /></Layout></ProtectedRoute>} />

      {/* Exit Management */}
      <Route path="/exits" element={<ProtectedRoute requiredRole={['hr_manager','admin','superadmin','hr_consultant_admin']}><Layout><ExitManagement /></Layout></ProtectedRoute>} />

      {/* Attendance */}
      <Route path="/attendance" element={<ProtectedRoute><Layout><AttendanceManagement /></Layout></ProtectedRoute>} />

      {/* Org Chart */}
      <Route path="/org-chart" element={<ProtectedRoute><Layout><OrgChartPage /></Layout></ProtectedRoute>} />

      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <ConfigProvider theme={theme}>
      <AuthProvider>
        <Router>
      <ScreenLock>
          <div className="App">
            <AppRoutes />
          </div>
        </ScreenLock>
    </Router>
      </AuthProvider>
    </ConfigProvider>
  );
};

export default App;
